import type { MarketplaceBrowseMapMarkerFragment } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import { centsToDollars } from 'components/marketplace/helpers/money';
import { buildPropertyPhotoUrl } from 'components/marketplace/property/helpers';
import type { ListingCardData } from 'components/marketplace/property/types';
import { formatCurrencyDollars } from 'helpers/formatters';

import type { ViewportPadding } from './googleSearchMapHandle';
import { PIN_TAIL_TIP } from './MapPinArtwork';
import type { MapPin } from './pins';

/** A first hover waits this long, so sweeping across pins does not flash previews. */
export const PIN_PREVIEW_SHOW_DELAY_MS = 150;
/** A pin entered this soon after a preview closed waits only the switch dwell. */
export const PIN_PREVIEW_SWITCH_MS = 300;
/**
 * A pin reached from a preview shows its own once the pointer rests on it this long. That is under
 * the roughly 100 ms at which a response stops feeling immediate, and longer than a sweep at 750 px/s
 * or faster stays on a 60 px price pin, so a sweep shows no preview for the pins it crosses.
 */
export const PIN_PREVIEW_SWITCH_DWELL_MS = 80;
/**
 * A preview downloads a photo that the list card has not loaded only after it has stayed up this
 * long, as long as a first hover waits, so a pointer that drifts across pins downloads no photo for
 * each of them.
 */
export const PIN_PREVIEW_PHOTO_DELAY_MS = 150;
/**
 * A pointer that moves straight from one pin onto the next leaves one and enters the other in a
 * single pointer move, well within this time. Separate pointer moves are a display frame or more
 * apart, which is 2 ms even at 500 Hz.
 */
const SAME_POINTER_MOVE_MS = 2;
const PIN_PREVIEW_GAP = 8;

export type PinPreviewTarget = { pin: MapPin; element: HTMLElement };

/** The shown preview. `loadPhoto` is true once it may download a photo the list card has not loaded. */
export type PinPreviewState = { target: PinPreviewTarget; loadPhoto: boolean };

/**
 * A hovered pin's home. A home from the loaded `results` has its full listing, and `photoUrl` is
 * the image its list card already shows, so the preview reuses that download; null when the card
 * has no loaded photo. A home outside them has only its map `marker` facts, which have no photo,
 * status labels, or payment estimate, so the preview requests its listing.
 */
export type PinPreviewListing =
  | { source: 'results'; listing: ListingCardData; photoUrl: string | null }
  | { source: 'marker'; listing: ListingCardData };

/** The listing a preview requested for a home outside the loaded results. */
export type PinPreviewRequest =
  | { status: 'pending' }
  | { status: 'loaded'; listing: ListingCardData }
  | { status: 'unavailable' };

/**
 * The preview photo: `card` is the photo the list card already shows, `first` is the home's first
 * photo, which the preview downloads itself, `pending` is a first photo that waits for
 * `loadPhoto`, and `none` is a home with no photo.
 */
type PinPreviewPhoto =
  | { kind: 'card'; url: string }
  | { kind: 'first'; url: string }
  | { kind: 'pending' }
  | { kind: 'none' };

export type PinPreviewContent =
  | { kind: 'listing'; listing: ListingCardData; photo: PinPreviewPhoto }
  | { kind: 'pin'; price: string };

/**
 * Decides which hovered price pin shows a preview. A first hover waits the show delay, and a pin
 * reached from a preview waits the shorter switch dwell. When the pointer moves straight from the
 * shown pin onto a neighbor, the preview stays up during the dwell, so it moves to the neighbor and
 * does not close and open again. After a dismissal the pin under the pointer stays without a
 * preview until the pointer leaves it.
 */
export const createPinPreviewController = () => {
  const listeners = new Set<(state: PinPreviewState | null) => void>();
  let shown: PinPreviewState | null = null;
  let pending: { target: PinPreviewTarget; timer: ReturnType<typeof setTimeout> } | null = null;
  let photoTimer: ReturnType<typeof setTimeout> | undefined;
  // Homes whose first photo a preview downloaded. The browser keeps that photo, so a later preview
  // of the same home shows it at once.
  const photoIds = new Set<string>();
  // The preview a leave closed last, and whether the pointer left that preview's own pin. Its time
  // comes from the monotonic clock, because a wall clock that steps back would make a later enter
  // look like part of the same pointer move.
  let closed: { state: PinPreviewState; at: number; ownPin: boolean } | null = null;
  let dismissedId: string | null = null;
  let paused = false;

  const cancelPending = () => {
    if (pending) clearTimeout(pending.timer);
    pending = null;
  };
  const publish = (state: PinPreviewState | null) => {
    shown = state;
    listeners.forEach((listener) => listener(state));
  };
  const hide = () => {
    clearTimeout(photoTimer);
    if (shown) publish(null);
  };
  const show = (target: PinPreviewTarget, photoAllowed = false) => {
    cancelPending();
    clearTimeout(photoTimer);
    const loadPhoto = photoAllowed || photoIds.has(target.pin.addressId);
    publish({ target, loadPhoto });
    if (loadPhoto) return;
    photoTimer = setTimeout(() => publish({ target, loadPhoto: true }), PIN_PREVIEW_PHOTO_DELAY_MS);
  };
  const showAfter = (target: PinPreviewTarget, delay: number) => {
    cancelPending();
    pending = { target, timer: setTimeout(() => show(target), delay) };
  };
  const dismiss = () => {
    dismissedId = (pending?.target ?? shown?.target)?.pin.addressId ?? dismissedId;
    cancelPending();
    closed = null;
    hide();
  };

  return {
    subscribe: (listener: (state: PinPreviewState | null) => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    enter: (target: PinPreviewTarget) => {
      if (paused || target.pin.addressId === dismissedId) return;
      const now = performance.now();
      // The leave and this enter come from one pointer move, so React renders the close and a
      // reopen together and the preview stays mounted. A photo it was already downloading stays.
      const straight = closed && now - closed.at < SAME_POINTER_MOVE_MS ? closed : null;
      if (straight?.state.target.pin.addressId === target.pin.addressId) {
        show(target, straight.state.loadPhoto);
        return;
      }
      if (straight?.ownPin) publish(straight.state);
      const switching =
        shown !== null || (closed !== null && now - closed.at < PIN_PREVIEW_SWITCH_MS);
      showAfter(target, switching ? PIN_PREVIEW_SWITCH_DWELL_MS : PIN_PREVIEW_SHOW_DELAY_MS);
    },
    leave: (addressId: string) => {
      if (addressId === dismissedId) dismissedId = null;
      const leftPending = pending?.target.pin.addressId === addressId;
      if (leftPending) cancelPending();
      if (!shown) return;
      const ownPin = shown.target.pin.addressId === addressId;
      // A preview kept up for a neighbor's dwell closes when the pointer leaves that neighbor.
      if (!ownPin && !leftPending) return;
      closed = { state: shown, at: performance.now(), ownPin };
      hide();
    },
    /** Records that a preview downloaded the home's first photo, so its next preview needs no wait. */
    markPhotoLoaded: (addressId: string) => {
      photoIds.add(addressId);
    },
    /** Hides the preview at once, for camera moves, Escape, and opening the home. */
    dismiss,
    /** Dismisses and ignores hovers until `resume`, for a map drag. */
    pause: () => {
      paused = true;
      dismiss();
    },
    resume: () => {
      paused = false;
    },
    dispose: () => {
      cancelPending();
      clearTimeout(photoTimer);
    },
  };
};

export type PinPreviewController = ReturnType<typeof createPinPreviewController>;

const pinPreviewPrice = (listPriceCents: number | null) => {
  if (listPriceCents == null) return 'Price coming soon';
  if (listPriceCents <= 0) return 'Call for price';
  return formatCurrencyDollars(listPriceCents / 100);
};

const pinPreviewPhoto = (
  listing: ListingCardData,
  cardPhotoUrl: string | null,
  loadPhoto: boolean,
): PinPreviewPhoto => {
  if (cardPhotoUrl != null) return { kind: 'card', url: cardPhotoUrl };
  const url = buildPropertyPhotoUrl(listing.photos[0], { preset: '3:2-sm' });
  if (url == null) return { kind: 'none' };
  return loadPhoto ? { kind: 'first', url } : { kind: 'pending' };
};

/** The facts a home's map marker carries, which its preview shows until the listing arrives. */
export const markerPinPreviewListing = (
  marker: MarketplaceBrowseMapMarkerFragment,
): PinPreviewListing => ({
  source: 'marker',
  listing: {
    addressToken: marker.addressId,
    baths: marker.bathrooms,
    beds: marker.bedrooms,
    latitude: marker.location.latitude,
    listPrice: centsToDollars(marker.listPriceCents),
    listingState: marker.listingState,
    livingSquareFootage: marker.totalLivingSquareFootage,
    longitude: marker.location.longitude,
    photos: marker.photoUrls ?? [],
    street: marker.street,
  },
});

type PinPreviewContentOptions = {
  pin: MapPin;
  /** The home from the loaded results or the map markers; null when the page has neither. */
  found: PinPreviewListing | null;
  /** The listing requested for a home outside the loaded results; null when none was requested. */
  request: PinPreviewRequest | null;
  /** True once the preview may download a photo that the list card has not loaded. */
  loadPhoto: boolean;
};

/**
 * A home from the loaded results shows its listing. A card that has not loaded its photo, such as
 * one past the list's prefetch range, gets the first photo at the card size so the preview still
 * looks like the list card. That photo waits for `loadPhoto` and shows as loading until then. The
 * browser downloads it once per home and serves later hovers from its cache.
 *
 * A home outside the loaded results shows its marker facts with a loading photo until its
 * requested listing arrives, and keeps those facts when the request fails. The pin's price is
 * left only for a home that the page has no marker for.
 */
export const pinPreviewContent = ({
  pin,
  found,
  request,
  loadPhoto,
}: PinPreviewContentOptions): PinPreviewContent => {
  if (found?.source === 'results') {
    return {
      kind: 'listing',
      listing: found.listing,
      photo: pinPreviewPhoto(found.listing, found.photoUrl, loadPhoto),
    };
  }
  if (request?.status === 'loaded') {
    return {
      kind: 'listing',
      listing: request.listing,
      photo: pinPreviewPhoto(request.listing, null, loadPhoto),
    };
  }
  if (found) {
    return {
      kind: 'listing',
      listing: found.listing,
      photo:
        request?.status === 'pending'
          ? { kind: 'pending' }
          : pinPreviewPhoto(found.listing, null, loadPhoto),
    };
  }
  return { kind: 'pin', price: pinPreviewPrice(pin.listPriceCents) };
};

export type PreviewBox = { left: number; top: number; right: number; bottom: number };

const PIN_PREVIEW_EDGE = 12;

export const pinPreviewArea = ({
  map,
  origin,
  covered,
}: {
  map: DOMRect;
  origin: DOMRect;
  covered: ViewportPadding;
}): PreviewBox => ({
  left: map.left - origin.left + (covered.left ?? 0) + PIN_PREVIEW_EDGE,
  top: map.top - origin.top + (covered.top ?? 0) + PIN_PREVIEW_EDGE,
  right: map.right - origin.left - (covered.right ?? 0) - PIN_PREVIEW_EDGE,
  bottom: map.bottom - origin.top - (covered.bottom ?? 0) - PIN_PREVIEW_EDGE,
});

/** The pin element's box stops at the bubble, so this adds the tail for a preview below to clear it. */
export const pricePinBox = (pin: DOMRect, origin: DOMRect): PreviewBox => ({
  left: pin.left - origin.left,
  right: pin.right - origin.left,
  top: pin.top - origin.top,
  bottom: pin.top - origin.top + PIN_TAIL_TIP,
});

export type PinPreviewPlacement = { left: number; top: number; originX: number; originY: number };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Puts the preview above the pin, below it when the top has no room, and beside
 * it when neither fits, always inside `area`. `originX`/`originY` is the pin's
 * point on the preview, for a scale that grows out of the pin. Null when the
 * area is smaller than the preview, or when no side has room clear of the pin,
 * because a preview over its own pin hides the price under the pointer.
 */
export const placePinPreview = ({
  pin,
  size,
  area,
}: {
  pin: PreviewBox;
  size: { width: number; height: number };
  area: PreviewBox;
}): PinPreviewPlacement | null => {
  const { width, height } = size;
  if (width <= 0 || height <= 0) return null;
  if (area.right - area.left < width || area.bottom - area.top < height) return null;
  const centerX = (pin.left + pin.right) / 2;
  const centerY = (pin.top + pin.bottom) / 2;
  const clampX = (left: number) => clamp(left, area.left, area.right - width);
  const above = pin.top - PIN_PREVIEW_GAP - height;
  const below = pin.bottom + PIN_PREVIEW_GAP;
  let left: number;
  let top: number;
  if (above >= area.top) {
    left = clampX(centerX - width / 2);
    top = above;
  } else if (below + height <= area.bottom) {
    left = clampX(centerX - width / 2);
    top = below;
  } else {
    const rightOfPin = Math.max(pin.right + PIN_PREVIEW_GAP, area.left);
    const leftOfPin = Math.min(pin.left - PIN_PREVIEW_GAP - width, area.right - width);
    if (rightOfPin + width <= area.right) left = rightOfPin;
    else if (leftOfPin >= area.left) left = leftOfPin;
    else return null;
    top = clamp(centerY - height / 2, area.top, area.bottom - height);
  }
  return {
    left,
    top,
    originX: clamp(centerX - left, 0, width),
    originY: clamp(centerY - top, 0, height),
  };
};
